import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";
import { getUserChecks } from "utils/AuthUtil";
import { orgPermissions, RESOURCE_KEYS } from "utils/OrgPermissions";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import ExlyTable from "../../../../common/Components/ExlyTable";
import useDesktopMediaQuery from "hooks/useDesktopMediaQuery";
import { Button } from "@my-scoot/component-library-legacy";
import commonStyles from "./CommonStyles.module.css";
import styles from "./ZoomAttendanceOfListing.module.css";
import {
  getColumnConfig,
  filterConfig,
  tabFilterConfig,
  tableChipsFormatter,
} from "./tableConfig";
import { ViewButton } from "./components/ViewButton";
import { CustomerAttendanceForListingPopup } from "./components/CustomerAttendanceForListingPopup";
import { Header } from "common/Components/Header";
import classnames from "classnames";
import {
  app_pathnames,
  app_route_keys,
  app_route_titles,
} from "constants/urlPaths";
import {
  empty_state_props,
  goBack,
  navigateBack,
  sendReminderToJoin,
  showSendReminderToJoinButton,
  showViewButton,
  useStyles,
} from "./ZoomAttendanceOfListing.helper";
import { SendReminderToJoinButton } from "./components/SendReminderToJoinButton";
import { ReminderToJoinPopup } from "./components/ReminderToJoinPopup";
import { LearnMorePopup } from "./components/LearnMorePopup";
import { useNotifications } from "utils/hooks";
import { zoomAttendanceOfListingExporter } from "utils/csvExporters/zoomAttendance/zoomAttendanceOfListing";
import { dataExists } from "utils/validations";
import { appendUrlParams } from "utils/urlUtils";
import { fetchListingDetails } from "utils/listing/fetchListingDetails";
import constants from "constants/constants";
import { LearnMorePopupWebinar } from "./components/LearnMorePopupWebinar";
import { webinarAttendanceExporter } from "utils/csvExporters/zoomAttendance/webinarAttendanceOfListing";
import { webinar_event_type } from "schedule-v2/constants";
import { logError } from "utils/error";
import { ListingMeetingDetailsTable } from "./components/ListingMeetingDetailsTable";
import { useListingMeetingDetails } from "./hooks/useListingMeetingDetails";

import { ListingHeaderStatsSection } from "./components/ListingHeaderStatsSection";
import LearnMoreCta from "features/Common/modules/LearnMore/modules/LearnMoreCta/LearnMoreCta";

import { SessionParticipantsPage } from "./components/SessionParticipantsPage";
import { dataProvider } from "data";
import { ZOOM_ATTENDANCE_CONSTANTS } from "./constants/ZoomAttendance.constants";
import { apiMethods } from "data/api.constants";

import ExlyLoader from "ui/modules/ExlyLoader";

const ZoomAttendanceOfListing = (props) => {
  const slotUuid = props.match?.params?.slotUuid;
  const history = useHistory();
  const { notify } = useNotifications();
  const isDesktop = useDesktopMediaQuery();
  const classes = useStyles();
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [dataOfZoomAttendanceOfListing, setDataOfZoomAttendanceOfListing] =
    useState({});
  const listingUuid = props.match.params.listing_uuid;
  const {
    listingName = "",
    listingType,
    metadata,
    coverImage,
    scheduleTypeLabel,
  } = dataOfZoomAttendanceOfListing;

  const [initialSessionCheckLoading, setInitialSessionCheckLoading] = useState(
    !slotUuid
  );

  const handleNavigateToSessionParticipants = (targetSlotUuid) => {
    history.replace(
      app_pathnames[app_route_keys.zoom_attendance_session_participants]({
        listingUuid,
        slotUuid: targetSlotUuid,
      })
    );
  };

  useEffect(() => {
    if (!listingUuid || slotUuid) {
      setInitialSessionCheckLoading(false);
      return;
    }
    dataProvider
      .custom_request(
        ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_MEETING_DETAILS,
        apiMethods.GET,
        {
          listing_uuid: listingUuid,
          page: ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.PAGE_ALL,
        }
      )
      .then((res) => {
        const meetingDetails = res?.data?.meeting_details || [];
        const singleSession =
          !checkIsArrayEmpty(meetingDetails) && meetingDetails.length === 1
            ? meetingDetails[0]
            : null;

        const isCompleted =
          singleSession &&
          String(singleSession?.status).toLowerCase() ===
            ZOOM_ATTENDANCE_CONSTANTS.ATTENDANCE_STATUS.COMPLETED;

        if (singleSession?.slot_uuid && isCompleted) {
          handleNavigateToSessionParticipants(singleSession.slot_uuid);
        } else {
          setInitialSessionCheckLoading(false);
        }
      })
      .catch((err) => {
        setInitialSessionCheckLoading(false);
        logError({
          error: err,
          when: "checking session count in ZoomAttendanceOfListing",
          occuredAt: "ZoomAttendanceOfListing/index.js",
        });
        notify(
          ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.EXPORT_FAILURE ||
            "Failed to load meeting details. Please try again.",
          "error"
        );
      });
  }, [listingUuid, slotUuid, history, notify]);

  useEffect(() => {
    if (!listingUuid) {
      goBack();
      return;
    }
    fetchListingDetails({ listingUuid })
      .then((apiResponse) => {
        const listingData = apiResponse?.data?.event || {};
        const listingName = listingData.title;
        const listingType = listingData.type;
        const metadata = listingData.metadata || {};
        const coverImage =
          listingData.cover_image || listingData.image_url || "";
        const scheduleTypeLabel =
          listingData.type_title ||
          listingData.schedule_type_label ||
          "Fixed Class";
        const newState = {
          listingUuid,
          listingName,
          listingType,
          metadata,
          coverImage,
          scheduleTypeLabel,
        };

        // listingType can be 0, so we check for dataExists(listingType)
        if (!listingName || !dataExists(listingType)) {
          throw new Error("invalid data: " + JSON.stringify(newState));
        }
        setDataOfZoomAttendanceOfListing(newState);
      })
      .catch((err) => {
        logError({
          error: err,
          when: "calling fetchListingDetails",
          occuredAt:
            "src/ui/pages/ZoomAttendance/ZoomAttendanceOfListing/index.js",
        });
        goBack();
      });

    (async () => {
      const liveFeatures = await getUserChecks();
      if (
        !liveFeatures?.is_zoom_attendance_enabled ||
        orgPermissions.isNavigationItemHidden(
          RESOURCE_KEYS.SECTIONS.ZOOM_ATTENDANCE
        )
      ) {
        history.push("/");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showLearnMorePopup, setShowLearnMorePopup] = useState(false);
  const onLearnMoreClick = () => {
    setShowLearnMorePopup(true);
  };

  const [
    customerAttendanceForListingPopupProps,
    setCustomerAttendanceForListingPopupProps,
  ] = useState({});

  const onViewButtonClick = (customerData) => {
    history.push({
      search: appendUrlParams("", {
        customer_uuid: customerData.customer_uuid,
      }),
    });
    setCustomerAttendanceForListingPopupProps({ show: true, customerData });
  };

  const onCloseCustomerAttendanceForListingPopup = () => {
    history.goBack();
    setCustomerAttendanceForListingPopupProps({ show: false });
  };

  const [reminderToJoinPopupProps, setReminderToJoinPopupProps] = useState({});

  const onSendReminderToJoinButtonClick = (customerData) => {
    sendReminderToJoin({
      listingUuid,
      customerUuid: customerData.customer_uuid,
    })
      .then(() => {
        setReminderToJoinPopupProps({ show: true, customerData });
      })
      .catch((err) => {
        const errorMsg = err?.message || "";
        if (
          !errorMsg.includes("Try sending communication to this user after")
        ) {
          logError({
            error: err,
            when: "calling sendReminderToJoin",
            occuredAt:
              "src/ui/pages/ZoomAttendance/ZoomAttendanceOfListing/index.js",
          });
        }
        notify(
          errorMsg || "An error occured. Please try again after some time.",
          "error"
        );
      });
  };

  const isWebinar = listingType === constants.scheduleTypesId.webinar;
  const isSingleDayWebinar =
    (metadata?.webinar_event_type ?? webinar_event_type.ONE_DAY_EVENT) ===
    webinar_event_type.ONE_DAY_EVENT;

  const LearnMoreToShow = isWebinar ? LearnMorePopupWebinar : LearnMorePopup;
  const backNavigation = selectedSession
    ? () => setSelectedSession(null)
    : isWebinar && isSingleDayWebinar
    ? () => navigateBack(listingUuid)
    : goBack;

  const handleBackToZoomAttendance = () => {
    history.push(app_pathnames[app_route_keys.zoom_attendance]());
  };

  const HeaderComponent = (
    <Header
      title={app_route_titles[app_route_keys.zoom_attendance]}
      hideBackButton={!isDesktop ? true : false}
      onBack={handleBackToZoomAttendance}
      right={<LearnMoreCta onClick={onLearnMoreClick} href="" />}
      classNames={{
        container: classnames("border-0 p-0", commonStyles.largeText),
      }}
    />
  );

  const { exportAttendance } = useListingMeetingDetails({ listingUuid });

  const handleExportAttendance = async ({ filterValues = {} }) => {
    const dataToExport = await exportAttendance({
      filterValues,
      isWebinar,
      notify,
    });
    if (!checkIsArrayEmpty(dataToExport)) {
      let finalExportData = dataToExport;
      if (selectedIds && selectedIds.length > 0) {
        const selectedSet = new Set(
          selectedIds.map((id) => String(id).toLowerCase().trim())
        );
        finalExportData = dataToExport.filter((item, index) => {
          const indexStr = String(index);
          const itemSlotUuid = item?.slot_uuid
            ? String(item.slot_uuid).toLowerCase().trim()
            : "";
          const itemId = item?.id ? String(item.id).toLowerCase().trim() : "";
          const itemSessionNo =
            item?.session_number !== undefined
              ? String(item.session_number).toLowerCase().trim()
              : "";
          return (
            selectedSet.has(indexStr) ||
            (itemSlotUuid && selectedSet.has(itemSlotUuid)) ||
            (itemId && selectedSet.has(itemId)) ||
            (itemSessionNo && selectedSet.has(itemSessionNo))
          );
        });
      }

      const exporterFunc = isWebinar
        ? webinarAttendanceExporter
        : zoomAttendanceOfListingExporter;
      exporterFunc(finalExportData);
    }
  };

  if (slotUuid) {
    return <SessionParticipantsPage {...props} />;
  }

  if (initialSessionCheckLoading) {
    return <ExlyLoader />;
  }

  const handleViewAttendees = (session) => {
    history.push(
      app_pathnames[app_route_keys.zoom_attendance_session_participants]({
        listingUuid,
        slotUuid: session?.slot_uuid,
      })
    );
  };

  return (
    <div>
      {!selectedSession ? (
        <div className="tw-mt-5">
          {isDesktop && (
            <div className="tw-mb-3 tw-px-5">{HeaderComponent}</div>
          )}
          <ListingHeaderStatsSection
            listingName={listingName}
            listingType={listingType}
            coverImage={coverImage}
            listingUuid={listingUuid}
            scheduleTypeLabel={scheduleTypeLabel}
          />
          <ListingMeetingDetailsTable
            {...props}
            selected={selectedIds}
            onRowSelected={(ids) => setSelectedIds(ids)}
            noExportButton={Boolean(false)}
            customHandleExport={handleExportAttendance}
            customBackAction={handleBackToZoomAttendance}
            tableTitle={!isDesktop ? HeaderComponent : null}
            onViewAttendees={handleViewAttendees}
          />
        </div>
      ) : (
        <ExlyTable
          ra_props={{
            ...props,
            exporter: isWebinar
              ? webinarAttendanceExporter
              : zoomAttendanceOfListingExporter,
          }}
          noExportButton={Boolean(false)}
          layoutProps={{
            secondaryComponent: isDesktop ? (
              <div>
                {orgPermissions.canSendEmail() && (
                  <Button
                    color="link"
                    onClick={onLearnMoreClick}
                    className={classnames(commonStyles.linkButton, "tw-p-0")}
                  >
                    <u>Learn More</u>
                  </Button>
                )}
              </div>
            ) : null,
            paddingDesktop: "0",
            paddingBottom: "120px",
            noMobileBoxShadow: true,
          }}
          customBackAction={backNavigation}
          columnConfig={getColumnConfig({
            isDesktop,
            onViewButtonClick,
            onSendReminderToJoinButtonClick,
            listingType,
            listingMetadata: metadata,
          })}
          className={styles.gap16}
          tableFilters={filterConfig}
          tabFilterConfig={tabFilterConfig}
          tableChipsFormatter={tableChipsFormatter}
          borderedFields
          fieldsLeftPadded
          drawerFieldsBordered
          fieldsAlignment="space-between"
          drawerFieldsAlignment="space-between"
          recordFooterVariant="secondary"
          getRecordFooterClassName={(record) =>
            showSendReminderToJoinButton({ record, listingType })
              ? classes.reminderToJoinButtonContainer
              : isWebinar && classes.displayNone
          }
          customPrimaryAction={{
            renderCustomAction: (record) => {
              if (listingType === constants.scheduleTypesId.webinar)
                return null;
              if (showViewButton(record)) {
                return (
                  <div className="tw-d-flex tw-justify-content-between tw-w-100 tw-mr-2">
                    <div>Action:</div>
                    <ViewButton
                      onClick={() => {
                        onViewButtonClick(record);
                      }}
                    />
                  </div>
                );
              }
              if (showSendReminderToJoinButton({ record, listingType })) {
                return (
                  <div
                    className={
                      "tw-d-flex tw-justify-content-end tw-w-100 tw-mr-2"
                    }
                  >
                    <SendReminderToJoinButton
                      onClick={() => {
                        onSendReminderToJoinButtonClick(record);
                      }}
                    />
                  </div>
                );
              }
              return null;
            },
          }}
          primaryKey="customer_uuid"
          {...empty_state_props}
        />
      )}
      {customerAttendanceForListingPopupProps.show && (
        <CustomerAttendanceForListingPopup
          open={customerAttendanceForListingPopupProps.show}
          customerData={customerAttendanceForListingPopupProps.customerData}
          listingUuid={listingUuid}
          listingName={listingName}
          onClose={onCloseCustomerAttendanceForListingPopup}
        />
      )}
      {reminderToJoinPopupProps.show && (
        <ReminderToJoinPopup
          open={reminderToJoinPopupProps.show}
          customerData={reminderToJoinPopupProps.customerData}
          onClose={() => {
            setReminderToJoinPopupProps({ show: false });
          }}
        />
      )}
      {showLearnMorePopup && (
        <LearnMoreToShow
          open={showLearnMorePopup}
          listingName={listingName}
          onClose={() => {
            setShowLearnMorePopup(false);
          }}
        />
      )}
    </div>
  );
};

export default withComponentLibraryTheme(ZoomAttendanceOfListing);
